import { addToast, Table, TablePagination } from '@octano/global-ui';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import DisplayError from '../../../../components/info/DisplayError';
import Loading from '../../../../components/info/Loading';
import TableEmptyContent from '../../../../components/text/TableEmptyContent';
import { PathsLayouts } from '../../../../config/routes';
import { useLoadingState } from '../../../../hooks/useLoadingState';
import { TranslationsKeys } from '../../../../locales/translations';
import {
  ContinuousEducationType,
  getStudyPlansListRequest,
  StudyPlanListFilters,
  StudyPlanListItem,
  StudyPlanVersionStatus,
  updateVersionStatusRequest,
} from '../api';
import { useStudyPlansColumns } from '../hooks/useStudyPlansColumns';

interface StudyPlansListProps {
  filters: StudyPlanListFilters;
}

const ITEMS_PER_PAGE = 10;

const StudyPlansList = ({ filters }: StudyPlansListProps) => {
  const { t } = useTranslation(
    TranslationsKeys.CONTINUOUS_EDUCATION_STUDY_PLANS,
  );
  const prefix = 'list';
  const history = useHistory();

  const [studyPlans, setStudyPlans] = useState<StudyPlanListItem[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [reload, setReload] = useState<boolean>(false);

  const {
    loading,
    setLoading,
    errorLoading,
    setErrorLoading,
  } = useLoadingState();

  const fetchStudyPlans = useCallback(
    async (page: number = 0) => {
      setLoading(true);
      const { data, error } = await getStudyPlansListRequest(
        filters,
        page,
        ITEMS_PER_PAGE,
      );

      setLoading(false);

      if (error) {
        setErrorLoading(error.code);
        return;
      }

      if (data) {
        setStudyPlans(data.data);
        setTotalItems(data.total);
        setTotalPages(Math.ceil(data.total / ITEMS_PER_PAGE));
      }
    },
    [filters, setLoading, setErrorLoading],
  );

  useEffect(() => {
    setCurrentPage(1);
    fetchStudyPlans(0);
  }, [fetchStudyPlans, reload]);

  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      fetchStudyPlans(page - 1);
    },
    [fetchStudyPlans],
  );

  const handleStatusChange = useCallback(
    async (versionId: number, newStatus: StudyPlanVersionStatus) => {
      const { error } = await updateVersionStatusRequest(versionId, newStatus);
      if (error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`common.msg.statusChangeError`),
          className: 'long-toast',
        });
        return;
      }
      setReload((prev) => !prev);
    },
    [t],
  );

  const goToManage = useCallback(
    (id: number, type: string | undefined) => {
      const path =
        type === ContinuousEducationType.COURSE
          ? `${PathsLayouts.continuousEducation}/study-plans/course/${id}`
          : `${PathsLayouts.continuousEducation}/study-plans/program/${id}`;
      history.push(path);
    },
    [history],
  );

  const columns = useStudyPlansColumns({
    onStatusChange: handleStatusChange,
    onManage: goToManage,
  });

  if (errorLoading) {
    return (
      <DisplayError
        insideCard
        textBody={errorLoading}
        retryAction={() => fetchStudyPlans(currentPage - 1)}
        loadingAction={loading}
      />
    );
  }

  if (loading && studyPlans.length === 0) {
    return <Loading insideCard />;
  }

  return (
    <div id={'curriculum-mention-list-table'}>
      <Table
        columns={columns}
        data={studyPlans}
        isLoadingResults={loading}
        noResultsText={
          <TableEmptyContent
            title={t(`${prefix}.emptyTitle`)}
            subtitle={t(`${prefix}.emptySubtitle`)}
          />
        }
      />
      {totalItems > ITEMS_PER_PAGE && (
        <TablePagination
          pagination={{
            currentPage: currentPage,
            itemsPerPage: ITEMS_PER_PAGE,
            onChangePage: handlePageChange,
            totalItems: totalItems,
            totalPages: totalPages,
          }}
        />
      )}
    </div>
  );
};

export default StudyPlansList;
